import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { BellOff, Check, CircleMinus, X, type LucideIcon } from "lucide-react";
import { userIdAtom } from "@/atoms/authAtoms";
import { Button, cn } from "@/design-system/components/react";
import { ackCase, notifyErrorText, postReceipt, type AckResult } from "./api";
import { alertSentText, clockTime, ownerName, personName } from "./format";
import { useNotifyOutlet } from "./NotifyLayout";
import {
  caseRef,
  notifyKeys,
  supersedesAck,
  useAckedCases,
  useCaseDetail,
  useRefetchMeOnForbidden,
} from "./queries";
import { canAck, canReadCases, pickSite, siteLabel } from "./site";
import { badgeState, StateBadge, TestTag } from "./StateBadge";
import type {
  AlertOutcome,
  AlertRecipient,
  NotifyCase,
  NotifyCaseDetail,
  NotifyEvent,
  NotifySiteMembership,
} from "./types";

/**
 * Records the receipt once when the page was opened from the push (`?via=push`),
 * for every role: the "who tapped the alert, and when" log. The first receipt
 * per person wins on the server, so a reload or reopen is harmless.
 */
function useAutoReceipt(siteId: string, studyUid: string, viaPush: boolean) {
  const refetchMeOnForbidden = useRefetchMeOnForbidden();
  const receipt = useMutation({
    mutationFn: () => postReceipt(siteId, studyUid),
    onError: refetchMeOnForbidden,
  });
  const sent = useRef<string | null>(null);
  const { mutate } = receipt;
  useEffect(() => {
    const ref = caseRef({ site_id: siteId, study_uid: studyUid });
    if (!viaPush || sent.current === ref) return;
    sent.current = ref;
    mutate();
  }, [siteId, studyUid, viaPush, mutate]);
  return receipt;
}

/** Clinicians and interventionalists: no case details in S1, only that their
 * receipt was recorded (or a Retry when that call failed). */
function RecipientView({
  site,
  viaPush,
  receipt,
}: {
  site: NotifySiteMembership;
  viaPush: boolean;
  receipt: ReturnType<typeof useAutoReceipt>;
}) {
  return (
    <main className="grid flex-1 content-center justify-items-center gap-3 px-6 pb-12 pt-6 text-center">
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-dim">
        {siteLabel(site)}
      </span>
      <h1 className="text-xl font-semibold">You received a Notify alert</h1>
      {!viaPush ? (
        <p className="max-w-[32ch] text-fg-muted">
          Open this alert from its notification to record your receipt.
        </p>
      ) : receipt.isSuccess ? (
        <>
          <p
            role="status"
            className="inline-flex items-center gap-1.5 rounded-[2px] bg-signal-tint px-3 py-1.5 font-mono text-sm font-medium tabular-nums text-signal-text-tint"
          >
            Received at {clockTime(receipt.data.at)}
            <Check aria-hidden className="size-4" />
          </p>
          <p className="max-w-[32ch] text-fg-muted">
            Your receipt was recorded when you opened this page. You can close it.
          </p>
        </>
      ) : receipt.isError ? (
        <>
          <div
            role="alert"
            className="grid w-full max-w-[280px] gap-0.5 rounded-[2px] border border-border-strong bg-surface-2 p-3 text-left text-[13px]"
          >
            <b className="font-semibold">Receipt not recorded</b>
            <span className="text-[12.5px] text-fg-muted">
              {notifyErrorText(receipt.error)}. Check your connection, then retry.
            </span>
          </div>
          <Button
            className="h-12 w-full max-w-[280px] text-[15px]"
            onClick={() => receipt.mutate()}
          >
            Retry
          </Button>
        </>
      ) : (
        <p role="status" className="text-fg-muted">
          Recording your receipt…
        </p>
      )}
    </main>
  );
}

/** A person as the timeline shows them: "you", else their id (events carry no names). */
const personText = (id: string | undefined, me: string | null) =>
  id && id === me ? "you" : id || "someone";

function eventText(event: NotifyEvent, me: string | null): string {
  switch (event.type) {
    case "flagged":
      return "Study flagged";
    case "alert_sent": {
      const count = event.data?.recipients?.length;
      return count ? `Alert sent to ${count} ${count === 1 ? "person" : "people"}` : "Alert sent";
    }
    case "alert_failed":
      return event.data?.reason ? `Alert not sent: ${event.data.reason}` : "Alert not sent";
    case "alert_skipped":
      return event.data?.reason ? `Alert skipped: ${event.data.reason}` : "Alert skipped";
    case "acknowledged":
      return `Acknowledged by ${personText(event.actor_person_id, me)}`;
    default:
      return event.type;
  }
}

/** The case's events, oldest first. Receipts are left out in S1: who opened a
 * case is not shown (backlog NB-26), only who acknowledged it. */
function Timeline({ events, me }: { events: NotifyEvent[]; me: string | null }) {
  const shown = events.filter((event) => event.type !== "received_ack");
  return (
    <div className="grid gap-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-dim">
        Timeline
      </span>
      <ol className="grid border-t border-border">
        {shown.map((event) => {
          const mine = !!me && event.actor_person_id === me;
          return (
            <li
              key={event.event_id}
              className={cn(
                "grid grid-cols-[48px_1fr] gap-3 border-b border-border py-2 text-[13px]",
                mine && "pl-2 shadow-[inset_2px_0_0_var(--color-signal)]",
              )}
            >
              <time dateTime={event.at} className="font-mono text-xs tabular-nums text-fg-muted">
                {clockTime(event.at)}
              </time>
              <div>
                {eventText(event, me)}
                <div className="font-mono text-[11px] text-fg-dim">
                  {[event.type, event.actor_person_id].filter(Boolean).join(" · ")}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** Each alert outcome as an icon and a text label (no hue: the label carries it). */
const OUTCOMES: Record<AlertOutcome, { icon: LucideIcon; label: string }> = {
  sent: { icon: Check, label: "sent" },
  failed: { icon: X, label: "not delivered" },
  no_device: { icon: BellOff, label: "alerts off" },
  not_tried: { icon: CircleMinus, label: "not tried" },
};

/** The latest alert's per-person result, when that event carries one. */
export function alertRecipients(events: NotifyEvent[]): AlertRecipient[] | null {
  const alert = events.findLast(
    (event) => event.type === "alert_sent" || event.type === "alert_failed",
  );
  return alert?.data?.people?.length ? alert.data.people : null;
}

/** "Love (you) ✓ sent · Jeff — alerts off …", one item per recipient. */
function Recipients({ people, me }: { people: AlertRecipient[]; me: string | null }) {
  return (
    <ul aria-label="Recipients" className="m-0 flex list-none flex-wrap gap-x-3 gap-y-1 p-0">
      {people.map((person) => {
        const { icon: Icon, label } = OUTCOMES[person.outcome] ?? OUTCOMES.not_tried;
        return (
          <li key={person.person_id} className="inline-flex items-center gap-1 whitespace-nowrap">
            <span className="text-fg">{personName(person.person_id, person.display_name, me)}</span>
            <Icon aria-hidden className="size-3 text-fg-muted" strokeWidth={2.5} />
            <span className="text-fg-muted">{label}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Who owns the case, as status text (no button once it has an owner). */
function OwnerNotice({ row, mine, name }: { row: NotifyCase; mine: boolean; name: string }) {
  const at = clockTime(row.acknowledged_at);
  return mine ? (
    <div
      role="status"
      className="grid gap-0.5 rounded-[2px] border-l-2 border-signal bg-signal-ambient p-3 text-[13px]"
    >
      <b className="font-semibold">You acknowledged at {at}</b>
      <span className="text-[12.5px] text-fg-muted">You own this case.</span>
    </div>
  ) : (
    <div
      role="status"
      className="grid gap-0.5 rounded-[2px] border border-border-strong bg-surface-2 p-3 text-[13px]"
    >
      <b className="font-semibold">
        Acknowledged by {name} at {at}
      </b>
      <span className="text-[12.5px] text-fg-muted">{name} owns this case.</span>
    </div>
  );
}

/** The case as an ack left it: ours, or from the 409 someone else's. */
const ackedCase = (row: NotifyCase, result: Exclude<AckResult, { kind: "retry" }>): NotifyCase =>
  result.kind === "acked"
    ? result.case
    : {
        ...row,
        state: "acknowledged",
        owner_person_id: result.ownerId,
        owner_display_name: result.ownerName,
        acknowledged_at: result.at,
      };

/** Radiologists and admins: the case, its owner, its timeline, and for a
 * radiologist Acknowledge while it has no owner. */
function ReaderCase({
  site,
  siteId,
  studyUid,
  receipt,
}: {
  site: NotifySiteMembership;
  siteId: string;
  studyUid: string;
  receipt: ReturnType<typeof useAutoReceipt>;
}) {
  const detail = useCaseDetail(siteId, studyUid, true);
  // A 403 may mean the caller's role changed: /me decides this page.
  const refetchMeOnForbidden = useRefetchMeOnForbidden();
  useEffect(() => {
    if (detail.error) refetchMeOnForbidden(detail.error);
  }, [detail.error, refetchMeOnForbidden]);
  const queryClient = useQueryClient();
  const user = useAtomValue(userIdAtom);
  const ref = caseRef({ site_id: siteId, study_uid: studyUid });
  const acked = useAckedCases();
  const served = detail.data?.case;
  // The server row once it has the ack (or a later state); until then this
  // tab's ack. The entry itself stays for the list, whose index read can lag
  // behind this consistent case read; the list drops it (useCaseList).
  const local = served && supersedesAck(served) ? undefined : acked.cases[ref];
  const myId = site.person_id;
  const [ackNote, setAckNote] = useState<string | null>(null);
  const key = notifyKeys.case(user, siteId, studyUid);

  const ack = useMutation({
    mutationFn: () => ackCase(siteId, studyUid),
    onMutate: () => setAckNote(null),
    onSuccess: (result) => {
      if (result.kind === "retry") return setAckNote(result.detail);
      const current = queryClient.getQueryData<NotifyCaseDetail>(key);
      if (!current) return;
      const row = ackedCase(current.case, result);
      acked.remember(row);
      queryClient.setQueryData<NotifyCaseDetail>(key, { ...current, case: row });
      // The case read is consistent: refetch for the new event.
      void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (error) => {
      refetchMeOnForbidden(error);
      setAckNote(notifyErrorText(error));
    },
  });

  if (detail.isLoading) return <p className="p-6 text-sm text-fg-dim">Loading case…</p>;
  if (detail.error || !detail.data)
    return (
      <p role="alert" className="p-6 text-sm text-fg-muted">
        Could not load this case: {notifyErrorText(detail.error)}
      </p>
    );

  const { events } = detail.data;
  const recipients = alertRecipients(events);
  const row = local ?? detail.data.case;
  const owner = ownerName(row, myId);
  const mine = !!owner && row.owner_person_id === myId;
  return (
    <main className="flex flex-1 flex-col gap-5 px-4 pb-6 pt-5 sm:p-6 md:grid md:grid-cols-2 md:items-start md:gap-10">
      <div className="grid min-w-0 gap-5">
        <div className="grid gap-1">
          <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-fg-dim">
            {siteLabel(site)}
            {row.is_test && (
              <>
                <span aria-hidden>·</span>
                <TestTag />
              </>
            )}
          </span>
          <h1 className="break-all font-mono text-[15px] font-semibold sm:text-lg">
            {row.study_uid}
          </h1>
          <p className="flex flex-wrap items-center gap-1.5 text-[13px] text-fg-muted">
            <StateBadge state={badgeState(row)} />
            {!owner && <span>· waiting for acknowledgement</span>}
          </p>
        </div>
        {receipt.isError && (
          // The automatic receipt (?via=push) failed: say so, small, with a retry.
          <p role="alert" className="text-[12.5px] text-fg-muted">
            Receipt not recorded.{" "}
            <button
              type="button"
              className="font-medium text-fg underline underline-offset-2"
              onClick={() => receipt.mutate()}
            >
              Retry
            </button>
          </p>
        )}
        {owner && <OwnerNotice row={row} mine={mine} name={owner} />}
        <dl className="m-0 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5">
          <dt className="text-xs text-fg-dim">Flagged</dt>
          <dd className="m-0 font-mono text-xs tabular-nums">{clockTime(row.flagged_at)}</dd>
          <dt className="text-xs text-fg-dim">Alert sent</dt>
          <dd className="m-0 font-mono text-xs tabular-nums">
            {alertSentText(row, { withFailedTime: true })}
          </dd>
          {recipients && (
            <>
              <dt className="text-xs text-fg-dim">Recipients</dt>
              <dd className="m-0 font-mono text-xs">
                <Recipients people={recipients} me={myId} />
              </dd>
            </>
          )}
          <dt className="text-xs text-fg-dim">Owner</dt>
          <dd className={cn("m-0 font-mono text-xs", !owner && "text-fg-dim")}>
            {owner ?? "none yet"}
          </dd>
        </dl>
        {ackNote && (
          <p
            role="alert"
            className="rounded-[2px] border border-border-strong bg-surface-2 p-3 text-[13px] text-fg-muted"
          >
            {ackNote}
          </p>
        )}
        {!owner && !canAck(site) && (
          <p className="text-[13px] text-fg-muted">Waiting for a radiologist to acknowledge.</p>
        )}
        {!owner && canAck(site) && (
          <div className="sticky bottom-0 -mx-4 grid border-t border-border bg-bg-2 px-4 py-3 md:static md:mx-0 md:flex md:border-0 md:bg-transparent md:p-0">
            <Button disabled={ack.isPending} onClick={() => ack.mutate()}>
              {ack.isPending ? "Acknowledging…" : "Acknowledge"}
            </Button>
          </div>
        )}
      </div>
      <Timeline events={events} me={myId} />
    </main>
  );
}

/**
 * A case page (/notify/cases/:siteId/:studyUid), opened from the push with
 * `?via=push`. Opening it from the push records the receipt automatically,
 * for every role; there is no Received button. The caller's roles on the
 * link's site decide the page: radiologists and admins see the case,
 * clinicians and interventionalists see only their receipt.
 */
export default function NotifyCaseDetail() {
  const { siteId = "", studyUid = "" } = useParams<{ siteId: string; studyUid: string }>();
  const [params] = useSearchParams();
  const viaPush = params.get("via") === "push";
  const { me } = useNotifyOutlet();
  const site = pickSite(me, siteId);
  // Before the role check: a site the caller is not on gets the server's 403.
  const receipt = useAutoReceipt(siteId, studyUid, viaPush);

  if (!site)
    return (
      <div role="alert" className="grid gap-1 p-6 text-sm text-fg-muted">
        <p className="font-semibold text-fg">You are not in the Notify directory for this site</p>
        <p>Ask the Notify admin to add you.</p>
      </div>
    );
  if (!canReadCases(site)) return <RecipientView site={site} viaPush={viaPush} receipt={receipt} />;
  return (
    <ReaderCase
      key={caseRef({ site_id: siteId, study_uid: studyUid })}
      site={site}
      siteId={siteId}
      studyUid={studyUid}
      receipt={receipt}
    />
  );
}
